import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Clock, Plus, ArrowsOut, X, Trash, User as UserIcon, Check, PencilSimple } from '@phosphor-icons/react';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';
import { getTagColor } from '../lib/tagColor';
import { getDueDateMeta } from '../lib/dueDateColor';
import { PriorityIcon } from './PriorityIcon';

const DEFAULT_COLUMNS = [
  { id: 'todo', label: 'To Do', dotColor: '#a1a1aa', tint: 'from-slate-50/60' },
  { id: 'in_progress', label: 'In Progress', dotColor: '#3b82f6', tint: 'from-blue-50/40' },
  { id: 'review', label: 'Review', dotColor: '#f59e0b', tint: 'from-amber-50/40' },
  { id: 'done', label: 'Done', dotColor: '#22c55e', tint: 'from-emerald-50/40' },
];

const PRIORITY_COLORS = {
  low: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-100' },
  medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-100' },
  high: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-100' },
  critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-100' },
};
const PRIORITY_LABELS = { low: 'Low', medium: 'Med', high: 'High', critical: 'Crit' };

// Compact relative-time formatter for "Created/Updated" card chips.
function relTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.floor(mo / 12)}y`;
}

// Colors for Severity / Reproducibility chips sourced from Bug Tracker template
const SEVERITY_COLORS = {
  Critical: 'bg-red-50 text-red-700 border-red-100',
  High:     'bg-orange-50 text-orange-700 border-orange-100',
  Medium:   'bg-amber-50 text-amber-700 border-amber-100',
  Low:      'bg-blue-50 text-blue-600 border-blue-100',
};

// Render template-specific badges (Severity / Story Points / Estimate) for a single task card.
// Now superseded by QuickSeverity + QuickStoryPoints + EstimateChip below.

// Jira-style work type icons
function WorkTypeIcon({ type, size = 16 }) {
  if (type === 'bug') {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" className="flex-shrink-0">
        <circle cx="8" cy="8" r="7.5" fill="#dc2626" />
        <circle cx="8" cy="8" r="3" fill="white" />
      </svg>
    );
  }
  if (type === 'story') {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" className="flex-shrink-0">
        <rect x="0.5" y="0.5" width="15" height="15" rx="2" fill="#16a34a" />
        <path d="M4 5h8M4 8h6M4 11h4" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className="flex-shrink-0">
      <rect x="0.5" y="0.5" width="15" height="15" rx="2" fill="#2563eb" />
      <path d="M4.5 8l2.5 2.5 4.5-5" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export { WorkTypeIcon };

// ---------- Quick inline editors (Jira/Asana-style) ----------

// Helper to swallow card-level click so the sheet doesn't open.
// IMPORTANT: do NOT call e.preventDefault() on the PopoverTrigger — Radix's
// composeEventHandlers skips its own toggle handler when defaultPrevented is true,
// which prevents the popover from ever opening. stopPropagation is enough to
// keep the click from bubbling up to the card.
const stop = (e) => { e.stopPropagation(); };

// Quick assignee picker: avatar chip → popover with member list.
function QuickAssignee({ task, members, getMemberAvatar, getMemberName, onTaskUpdate, canEdit }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const current = task.assignee_id;
  const filtered = (members || []).filter(m => m.name?.toLowerCase().includes(q.toLowerCase()));

  const pick = async (id) => { setOpen(false); await onTaskUpdate(task.task_id, { assignee_id: id }); };

  const curName = getMemberName(current);
  const curStyle = getAvatarStyle(current || curName);
  const trigger = current ? (
    <Avatar className={`h-6 w-6 ring-2 ring-white cursor-pointer hover:${curStyle.ring} transition-shadow`} title={curName}>
      <AvatarImage src={getMemberAvatar(current)} />
      <AvatarFallback className={`${curStyle.bg} text-white text-[9px] font-bold`}>{getInitials(curName)}</AvatarFallback>
    </Avatar>
  ) : (
    <div className="h-6 w-6 rounded-full border border-dashed border-zinc-300 flex items-center justify-center text-zinc-400 hover:border-zinc-400 hover:text-zinc-600 cursor-pointer" title="Assign">
      <UserIcon size={11} />
    </div>
  );

  if (!canEdit) return trigger;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild onClick={stop}>
        <button type="button" data-testid={`quick-assignee-${task.task_id}`} className="flex items-center">{trigger}</button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1" onClick={stop}>
        <Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="h-7 text-[12px] mb-1" />
        <div className="max-h-56 overflow-y-auto">
          <button type="button" onClick={() => pick(null)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-100 text-left">
            <div className="h-6 w-6 rounded-full border border-dashed border-zinc-300 flex items-center justify-center text-zinc-400"><UserIcon size={12} /></div>
            <span className="text-[12px] text-zinc-700">Unassigned</span>
            {!current && <Check size={12} className="ml-auto text-blue-600" />}
          </button>
          {filtered.map(m => {
            const s = getAvatarStyle(m.user_id || m.name);
            return (
              <button key={m.user_id} type="button" onClick={() => pick(m.user_id)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-100 text-left">
                <Avatar className="h-6 w-6"><AvatarImage src={m.avatar} /><AvatarFallback className={`${s.bg} text-white text-[9px] font-bold`}>{getInitials(m.name)}</AvatarFallback></Avatar>
                <span className="text-[12px] text-zinc-700 truncate">{m.name}</span>
                {current === m.user_id && <Check size={12} className="ml-auto text-blue-600" />}
              </button>
            );
          })}
          {filtered.length === 0 && <p className="text-[11px] text-zinc-400 text-center py-3">No matches</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Quick due-date picker: click date chip → native date input popover.
function QuickDueDate({ task, onTaskUpdate, canEdit }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(task.due_date?.slice(0, 10) || '');

  const save = async () => {
    setOpen(false);
    await onTaskUpdate(task.task_id, { due_date: val || null });
  };
  const clear = async () => {
    setVal(''); setOpen(false);
    await onTaskUpdate(task.task_id, { due_date: null });
  };

  // Color the chip according to urgency — overdue = red, today = orange,
  // ≤2 days = amber, future = neutral. Done tasks keep the neutral color
  // (no point shouting about a red overdue date on a closed ticket).
  const meta = !task.due_date || task.status === 'done' ? null : getDueDateMeta(task.due_date);
  const label = task.due_date ? (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 h-[18px] rounded-md whitespace-nowrap hover:brightness-95 ${meta ? meta.className : 'text-zinc-500 ring-1 ring-zinc-200 bg-zinc-50'}`}
      title={meta?.bucket === 'overdue' ? `Overdue · ${meta.label}` : new Date(task.due_date).toLocaleDateString()}
    >
      <Clock size={10} />{meta ? meta.label : new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
    </span>
  ) : (
    <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 h-[18px] rounded-md whitespace-nowrap text-zinc-400 ring-1 ring-dashed ring-zinc-200 hover:text-blue-600 hover:ring-blue-200">
      <Clock size={10} />Due
    </span>
  );

  if (!canEdit) return task.due_date ? label : null;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setVal(task.due_date?.slice(0, 10) || ''); }}>
      <PopoverTrigger asChild onClick={stop}>
        <button type="button" data-testid={`quick-due-${task.task_id}`} className="flex items-center">{label}</button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2" onClick={stop}>
        <div className="flex items-center gap-1">
          <Input type="date" min={new Date().toISOString().split('T')[0]} value={val} onChange={e => setVal(e.target.value)} autoFocus className="h-8 text-[12px]" />
          <button type="button" onClick={save} className="h-8 px-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-[11px] font-medium">Save</button>
          {task.due_date && <button type="button" onClick={clear} title="Clear" className="h-8 px-2 text-zinc-500 hover:text-red-600 rounded"><X size={12} /></button>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Quick severity: click chip → popover with Critical/High/Medium/Low.
const SEVERITY_OPTIONS = ['Critical', 'High', 'Medium', 'Low'];
function QuickSeverity({ task, customFields, onTaskUpdate, canEdit }) {
  const [open, setOpen] = useState(false);
  const def = customFields?.find(f => f.name === 'Severity');
  if (!def) return null;
  const current = task.custom_fields?.[def.field_id];

  const pick = async (v) => {
    setOpen(false);
    const next = { ...(task.custom_fields || {}), [def.field_id]: v };
    await onTaskUpdate(task.task_id, { custom_fields: next });
  };

  const chip = current && SEVERITY_COLORS[current]
    ? <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${SEVERITY_COLORS[current]} hover:brightness-95`}>{current}</span>
    : <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border border-dashed border-zinc-300 text-zinc-400 hover:border-zinc-400 hover:text-zinc-600">Severity</span>;

  if (!canEdit) return current ? chip : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild onClick={stop}>
        <button type="button" data-testid={`quick-severity-${task.task_id}`}>{chip}</button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-40 p-1" onClick={stop}>
        {SEVERITY_OPTIONS.map(opt => (
          <button key={opt} type="button" onClick={() => pick(opt)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-100 text-left">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${SEVERITY_COLORS[opt]}`}>{opt}</span>
            {current === opt && <Check size={12} className="ml-auto text-blue-600" />}
          </button>
        ))}
        {current && (
          <button type="button" onClick={() => pick('')} className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-100 text-[11px] text-zinc-500 mt-1 border-t pt-2">
            <X size={11} /> Clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Quick story points: click chip → number input popover.
function QuickStoryPoints({ task, customFields, onTaskUpdate, canEdit }) {
  const [open, setOpen] = useState(false);
  const def = customFields?.find(f => f.name === 'Story Points');
  const [val, setVal] = useState('');
  if (!def) return null;
  const current = task.custom_fields?.[def.field_id];

  const save = async () => {
    setOpen(false);
    const next = { ...(task.custom_fields || {}), [def.field_id]: val === '' ? null : Number(val) };
    await onTaskUpdate(task.task_id, { custom_fields: next });
  };

  const chip = current != null && current !== ''
    ? <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 text-[10px] font-bold hover:brightness-95">{current}</span>
    : <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full border border-dashed border-zinc-300 text-zinc-400 text-[10px] font-bold hover:border-zinc-400 hover:text-zinc-600">SP</span>;

  if (!canEdit) return current != null && current !== '' ? chip : null;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setVal(current != null ? String(current) : ''); }}>
      <PopoverTrigger asChild onClick={stop}>
        <button type="button" data-testid={`quick-sp-${task.task_id}`}>{chip}</button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-40 p-2" onClick={stop}>
        <div className="flex items-center gap-1">
          <Input type="number" min="0" value={val} onChange={e => setVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setOpen(false); }}
            placeholder="SP" autoFocus className="h-8 text-[12px]" />
          <button type="button" onClick={save} className="h-8 px-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-[11px] font-medium">Save</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Quick estimate chip (read-only fallback, kept as label).
function EstimateChip({ task, customFields }) {
  const def = customFields?.find(f => f.name === 'Estimate (hours)');
  if (!def) return null;
  const v = task.custom_fields?.[def.field_id];
  if (v == null || v === '') return null;
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-100" title={`Estimate: ${v}h`} data-testid="card-estimate">
      {v}h
    </span>
  );
}

export default function KanbanBoard({ tasks, columns: customColumns, statusOrder, project, onStatusChange, onReorderTasks, onTaskClick, onOpenFullScreen, getMemberName, getMemberAvatar, canEdit, onQuickAdd, onAddColumn, onDeleteColumn, onRenameColumn, onReorderColumns, customFields, members, onTaskUpdate, selectedTaskId }) {
  const allColumns = [...DEFAULT_COLUMNS];
  if (customColumns?.length) {
    customColumns.forEach(cs => {
      if (!allColumns.find(c => c.id === cs.id)) {
        allColumns.push({ id: cs.id, label: cs.label, dotColor: cs.color || '#6b7280' });
      }
    });
  }
  // Apply persisted column order (saved on project.status_order). Unknown
  // ids in status_order are dropped; columns missing from status_order are
  // appended at the end so newly-added columns still appear.
  if (Array.isArray(statusOrder) && statusOrder.length) {
    const byId = new Map(allColumns.map(c => [c.id, c]));
    const ordered = [];
    statusOrder.forEach(id => { if (byId.has(id)) { ordered.push(byId.get(id)); byId.delete(id); } });
    byId.forEach(c => ordered.push(c));
    allColumns.length = 0;
    allColumns.push(...ordered);
  }

  // Card-layout configuration. Project owners/admins choose which fields
  // appear on each Kanban card via Project Settings → Card layout. We
  // resolve a sensible default that matches the user's "5 stats in the
  // bottom strip" spec when nothing is configured yet.
  const DEFAULT_CARD_FIELDS = ['key', 'due_date', 'priority', 'story_points', 'assignee'];
  const cardFields = (Array.isArray(project?.card_fields) && project.card_fields.length)
    ? project.card_fields
    : DEFAULT_CARD_FIELDS;
  const showField = (id) => cardFields.includes(id);

  // Build a map of subtask counts (total + done) per parent task_id so we
  // can show a "🧩 N/M" badge on the parent card. Subtasks themselves are
  // hidden from the board (Jira-style) — they're managed inside the
  // parent's task sheet.
  const subtaskCounts = (() => {
    const map = new Map();
    for (const t of tasks) {
      if (!t.parent_task_id) continue;
      const cur = map.get(t.parent_task_id) || { total: 0, done: 0 };
      cur.total += 1;
      if (t.status === 'done') cur.done += 1;
      map.set(t.parent_task_id, cur);
    }
    return map;
  })();

  const getTasksByStatus = (status) => tasks
    // Hide subtasks — only top-level work items appear on the board.
    .filter(t => t.status === status && !t.parent_task_id)
    .sort((a, b) => {
      const ao = a.order ?? 0, bo = b.order ?? 0;
      if (ao !== bo) return ao - bo;
      // Stable fallback so equal-order tasks don't jitter between renders.
      return String(a.task_id).localeCompare(String(b.task_id));
    });
  const [hoverCol, setHoverCol] = useState(null);
  const [hoverHeader, setHoverHeader] = useState(null);
  const [renamingCol, setRenamingCol] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [addingColumn, setAddingColumn] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [newColColor, setNewColColor] = useState('#6b7280');
  // Column drag state
  const [dragColIdx, setDragColIdx] = useState(null);
  const [dragOverColIdx, setDragOverColIdx] = useState(null);
  const [colOrder, setColOrder] = useState(null);

  const orderedColumns = colOrder || allColumns;

  const onDragEnd = (result) => {
    if (!result.destination || !canEdit) return;
    onStatusChange(result.draggableId, result.destination.droppableId);
  };

  const handleColDragStart = (idx) => { setDragColIdx(idx); };
  const handleColDragOver = (e, idx) => { e.preventDefault(); setDragOverColIdx(idx); };
  const handleColDrop = (idx) => {
    if (dragColIdx === null || dragColIdx === idx) { setDragColIdx(null); setDragOverColIdx(null); return; }
    const reordered = [...orderedColumns];
    const [moved] = reordered.splice(dragColIdx, 1);
    reordered.splice(idx, 0, moved);
    setColOrder(reordered);
    setDragColIdx(null);
    setDragOverColIdx(null);
    if (onReorderColumns) onReorderColumns(reordered.map(c => c.id));
  };
  const handleColDragEnd = () => { setDragColIdx(null); setDragOverColIdx(null); };

  const handleTaskDragEnd = (result) => {
    if (!result.destination || !canEdit) return;
    const src = result.source;
    const dst = result.destination;
    // Same column → reorder (user dragged card up/down within the same status).
    if (src.droppableId === dst.droppableId) {
      if (src.index === dst.index) return; // no-op
      if (onReorderTasks) {
        const columnTasks = getTasksByStatus(src.droppableId);
        const reordered = [...columnTasks];
        const [moved] = reordered.splice(src.index, 1);
        reordered.splice(dst.index, 0, moved);
        onReorderTasks(reordered.map(t => t.task_id));
      }
      return;
    }
    // Different column → status change.
    onStatusChange(result.draggableId, dst.droppableId);
  };

  const handleCreateColumn = () => {
    if (!newColName.trim()) return;
    onAddColumn(newColName.trim(), newColColor);
    setNewColName('');
    setAddingColumn(false);
  };

  const COLUMN_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280'];

  return (
    <DragDropContext onDragEnd={handleTaskDragEnd}>
      <div className="flex h-full overflow-x-auto px-6 py-5 gap-4 bg-white kanban-scroll" data-testid="kanban-board">
        {orderedColumns.map((col, colIdx) => {
          const columnTasks = getTasksByStatus(col.id);
          const isDragTarget = dragOverColIdx === colIdx && dragColIdx !== colIdx;
          const colTint = col.tint || 'from-slate-50/40';
          return (
            <div key={col.id}
              className={`flex-shrink-0 w-[300px] flex flex-col transition-all duration-150 ${isDragTarget ? 'ring-2 ring-blue-300 rounded-xl' : ''} ${dragColIdx === colIdx ? 'opacity-50' : ''}`}
              data-testid={`kanban-column-${col.id}`}
              draggable={canEdit && renamingCol !== col.id}
              onDragStart={() => handleColDragStart(colIdx)}
              onDragOver={(e) => handleColDragOver(e, colIdx)}
              onDrop={() => handleColDrop(colIdx)}
              onDragEnd={handleColDragEnd}>
              <div className={`flex items-center gap-2 mb-2 px-2 ${renamingCol === col.id ? '' : 'cursor-grab active:cursor-grabbing'}`}
                onMouseEnter={() => setHoverHeader(col.id)} onMouseLeave={() => setHoverHeader(null)}>
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: col.dotColor }} />
                {renamingCol === col.id ? (
                  <input
                    autoFocus
                    value={renameVal}
                    onChange={(e) => setRenameVal(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { onRenameColumn && onRenameColumn(col.id, renameVal); setRenamingCol(null); }
                      if (e.key === 'Escape') setRenamingCol(null);
                    }}
                    onBlur={() => {
                      if (renameVal.trim() && renameVal.trim() !== col.label) onRenameColumn && onRenameColumn(col.id, renameVal);
                      setRenamingCol(null);
                    }}
                    className="text-[11px] font-bold text-slate-700 uppercase tracking-[0.08em] bg-white border border-blue-300 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-blue-400 w-40"
                    data-testid={`rename-column-input-${col.id}`}
                  />
                ) : (
                  <h3 className="text-[11px] font-bold text-slate-700 uppercase tracking-[0.08em]">{col.label}</h3>
                )}
                <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded-full px-1.5 py-0.5 min-w-[20px] text-center">{columnTasks.length}</span>
                {canEdit && hoverHeader === col.id && renamingCol !== col.id && !['todo', 'in_progress', 'review', 'done'].includes(col.id) && (
                  <div className="ml-auto flex items-center gap-0.5">
                    {onRenameColumn && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setRenameVal(col.label); setRenamingCol(col.id); }}
                        className="h-6 w-6 flex items-center justify-center rounded hover:bg-blue-50 text-zinc-400 hover:text-blue-600 transition-colors"
                        title="Rename column"
                        data-testid={`rename-column-${col.id}`}>
                        <PencilSimple size={12} />
                      </button>
                    )}
                    {onDeleteColumn && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDeleteColumn(col.id); }}
                        className="h-6 w-6 flex items-center justify-center rounded hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors"
                        title="Delete column"
                        data-testid={`delete-column-${col.id}`}>
                        <Trash size={12} />
                      </button>
                    )}
                  </div>
                )}
                {canEdit && hoverHeader === col.id && renamingCol !== col.id && ['todo', 'in_progress', 'review', 'done'].includes(col.id) && (
                  <span className="text-[9px] text-zinc-300 ml-auto select-none">drag to reorder</span>
                )}
              </div>
              <div className="h-px bg-gradient-to-r from-slate-200/70 via-slate-200/40 to-transparent mb-2" />
              <Droppable droppableId={col.id}>
                {(provided, snapshot) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}
                    className={`flex-1 rounded-xl p-2 space-y-2 overflow-y-auto transition-colors duration-200
                      ${snapshot.isDraggingOver ? 'bg-blue-50 ring-1 ring-blue-200' : `bg-gradient-to-b ${colTint} to-transparent`}`}
                    style={{ minHeight: 120 }}
                    onMouseEnter={() => setHoverCol(col.id)} onMouseLeave={() => setHoverCol(null)}>
                    {columnTasks.map((task, index) => (
                      <Draggable key={task.task_id} draggableId={task.task_id} index={index} isDragDisabled={!canEdit}>
                        {(provided, snapshot) => {
                          const isSelected = selectedTaskId === task.task_id;
                          return (
                          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                            data-testid={`task-card-${task.task_id}`}
                            data-selected={isSelected ? 'true' : 'false'}
                            onClick={() => onTaskClick(task)}
                            className={`rounded-xl p-3.5 cursor-pointer group relative transition-all duration-200
                              ${snapshot.isDragging
                                ? 'bg-white shadow-xl scale-[1.02] ring-2 ring-blue-400/40 rotate-[0.5deg] border border-slate-200'
                                : isSelected
                                  ? 'bg-blue-50/70 ring-2 ring-blue-400 border border-blue-200 shadow-[0_2px_8px_rgba(59,130,246,0.12)]'
                                  : 'bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:bg-slate-100 hover:border-slate-300 hover:shadow-[0_4px_14px_rgba(15,23,42,0.10)] hover:-translate-y-0.5'}`}>
                            <button data-testid={`fullscreen-${task.task_id}`} onClick={(e) => { e.stopPropagation(); onOpenFullScreen(task.task_id); }}
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all" title="Open full screen">
                              <ArrowsOut size={12} />
                            </button>
                            {/* Title row — work-type icon + title only.
                                All other stats live in the bottom strip
                                so the card has a single, consistent
                                visual rhythm regardless of project. */}
                            <div className="flex items-start gap-2 pr-6">
                              <div className="pt-0.5 flex-shrink-0"><WorkTypeIcon type={task.work_type || 'task'} size={14} /></div>
                              <p className="text-[13px] font-medium leading-snug text-slate-800 flex-1">{task.title}</p>
                            </div>
                            {/* Optional tags row (only when admins enable
                                "Tags" in Project Settings → Card layout). */}
                            {showField('tags') && task.tags?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2.5" data-testid={`card-tags-${task.task_id}`}>
                                {task.tags.slice(0, 3).map(tag => {
                                  const c = getTagColor(tag);
                                  return (
                                    <span key={tag} className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold ring-1 ${c.bg} ${c.text} ${c.ring}`}>{tag}</span>
                                  );
                                })}
                                {task.tags.length > 3 && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-slate-50 text-slate-500 ring-1 ring-slate-200" title={task.tags.slice(3).join(', ')}>+{task.tags.length - 3}</span>
                                )}
                              </div>
                            )}
                            {/* Bottom strip — admin-controlled. Default:
                                key · due · priority · SP · assignee.
                                Uses flex-wrap with row+col gaps so cards
                                with many enabled fields wrap cleanly
                                (no awkward leading dots, no truncated text).
                                Each chip is `whitespace-nowrap` so phrases
                                like "0h spent" stay on one line. */}
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-3 pt-2 border-t border-slate-50 text-[10px] text-slate-400">
                              {showField('key') && (
                                <span className={`font-mono whitespace-nowrap ${task.status === 'done' ? 'line-through' : ''}`} data-testid={`card-key-${task.task_id}`}>{task.task_key}</span>
                              )}
                              {showField('due_date') && (
                                <QuickDueDate task={task} onTaskUpdate={onTaskUpdate} canEdit={canEdit} />
                              )}
                              {showField('priority') && task.priority && (
                                <span className="inline-flex items-center" title={`Priority: ${task.priority}`} data-testid={`card-priority-${task.task_id}`}>
                                  <PriorityIcon priority={task.priority} size={12} />
                                </span>
                              )}
                              {showField('story_points') && (
                                <span data-testid={`card-sp-${task.task_id}`}><QuickStoryPoints task={task} customFields={customFields} onTaskUpdate={onTaskUpdate} canEdit={canEdit} /></span>
                              )}
                              {/* Relationship indicators — always shown
                                  when present (not gated by card_fields)
                                  because they're structural signals
                                  (subtasks, linked issues), not optional
                                  stats. Mirrors Jira's behavior. */}
                              {subtaskCounts.has(task.task_id) && (() => {
                                const c = subtaskCounts.get(task.task_id);
                                const allDone = c.done === c.total && c.total > 0;
                                return (
                                  <span
                                    className={`inline-flex items-center gap-0.5 px-1.5 h-[18px] rounded-md text-[10px] font-semibold ring-1 whitespace-nowrap ${allDone ? 'bg-emerald-50 text-emerald-700 ring-emerald-100' : 'bg-slate-50 text-slate-600 ring-slate-100'}`}
                                    title={`${c.done} of ${c.total} subtasks done`}
                                    data-testid={`subtask-badge-${task.task_id}`}
                                  >
                                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="-ml-0.5">
                                      <rect x="2" y="2" width="6" height="6" rx="1" /><rect x="8" y="8" width="6" height="6" rx="1" /><path d="M5 8v3a1 1 0 0 0 1 1h2" />
                                    </svg>
                                    {c.done}/{c.total}
                                  </span>
                                );
                              })()}
                              {(task.link_count || 0) > 0 && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1.5 h-[18px] rounded-md text-[10px] font-semibold bg-amber-50 text-amber-700 ring-1 ring-amber-100 whitespace-nowrap"
                                  title={`${task.link_count} linked issue${task.link_count === 1 ? '' : 's'} (blocks / is blocked by / relates to)`}
                                  data-testid={`link-badge-${task.task_id}`}
                                >
                                  <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="-ml-0.5">
                                    <path d="M6.5 9.5a2.5 2.5 0 0 0 3.54 0l2.5-2.5a2.5 2.5 0 1 0-3.54-3.54l-1 1" />
                                    <path d="M9.5 6.5a2.5 2.5 0 0 0-3.54 0l-2.5 2.5a2.5 2.5 0 1 0 3.54 3.54l1-1" />
                                  </svg>
                                  {task.link_count}
                                </span>
                              )}
                              {showField('reporter') && task.reporter_id && (() => {
                                const reporter = members?.find(m => m.user_id === task.reporter_id);
                                return (
                                  <span className="inline-flex items-center gap-1" title={`Reporter: ${reporter?.name || 'Unknown'}`} data-testid={`card-reporter-${task.task_id}`}>
                                    <Avatar className="h-3.5 w-3.5"><AvatarImage src={getMemberAvatar?.(task.reporter_id)} /><AvatarFallback className={`${getAvatarStyle(task.reporter_id).bg} text-white text-[7px] font-bold`}>{getInitials(reporter?.name)}</AvatarFallback></Avatar>
                                  </span>
                                );
                              })()}
                              {showField('sprint') && task.sprint_id && (
                                <span className="inline-flex items-center px-1.5 h-[18px] rounded-md text-[10px] font-medium bg-violet-50 text-violet-700 ring-1 ring-violet-100 whitespace-nowrap" title="Sprint" data-testid={`card-sprint-${task.task_id}`}>
                                  {String(task.sprint_id).slice(-6)}
                                </span>
                              )}
                              {showField('severity') && task.custom_fields?.severity && (
                                <span className={`inline-flex items-center px-1.5 h-[18px] rounded-md text-[10px] font-semibold ring-1 whitespace-nowrap ${SEVERITY_COLORS[task.custom_fields.severity] || 'bg-slate-50 text-slate-600 ring-slate-100'}`} title={`Severity: ${task.custom_fields.severity}`} data-testid={`card-severity-${task.task_id}`}>
                                  {task.custom_fields.severity}
                                </span>
                              )}
                              {showField('estimate') && (task.original_estimate_hours ?? null) !== null && (
                                <span className="inline-flex items-center text-[10px] text-slate-500 whitespace-nowrap" title="Original estimate" data-testid={`card-estimate-${task.task_id}`}>
                                  ⏱{task.original_estimate_hours}h
                                </span>
                              )}
                              {showField('time_spent') && (task.time_spent_hours ?? null) !== null && (
                                <span className="inline-flex items-center text-[10px] text-emerald-600 font-medium whitespace-nowrap" title="Time spent" data-testid={`card-spent-${task.task_id}`}>
                                  {task.time_spent_hours}h spent
                                </span>
                              )}
                              {showField('created_at') && task.created_at && (
                                <span className="text-[10px] text-slate-400 whitespace-nowrap" title={`Created ${new Date(task.created_at).toLocaleString()}`} data-testid={`card-created-${task.task_id}`}>
                                  +{relTime(task.created_at)}
                                </span>
                              )}
                              {showField('updated_at') && task.updated_at && (
                                <span className="text-[10px] text-slate-400 whitespace-nowrap" title={`Updated ${new Date(task.updated_at).toLocaleString()}`} data-testid={`card-updated-${task.task_id}`}>
                                  ~{relTime(task.updated_at)}
                                </span>
                              )}
                              {/* Custom fields — admin-toggleable. We render `label: value` chips
                                  for any project-defined custom field IDs in cardFields. */}
                              {(customFields || []).filter(cf => cardFields.includes(cf.field_id)).map(cf => {
                                const v = task.custom_fields?.[cf.field_id];
                                if (v === undefined || v === null || v === '') return null;
                                let display = String(v);
                                if (cf.type === 'date' && /^\d{4}-\d{2}-\d{2}/.test(display)) {
                                  display = new Date(display).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
                                }
                                if (display.length > 18) display = display.slice(0, 17) + '…';
                                return (
                                  <span key={cf.field_id} className="inline-flex items-center gap-1 px-1.5 h-[18px] rounded-md text-[10px] bg-violet-50 text-violet-700 ring-1 ring-violet-100 whitespace-nowrap" title={`${cf.name}: ${v}`} data-testid={`card-cf-${task.task_id}-${cf.field_id}`}>
                                    <span className="font-semibold">{cf.name}:</span>
                                    <span>{display}</span>
                                  </span>
                                );
                              })}
                              {showField('assignee') && (
                                <span className="ml-auto" data-testid={`card-assignee-${task.task_id}`}>
                                  <QuickAssignee task={task} members={members} getMemberAvatar={getMemberAvatar} getMemberName={getMemberName} onTaskUpdate={onTaskUpdate} canEdit={canEdit} />
                                </span>
                              )}
                            </div>
                          </div>
                        );}}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {canEdit && (
                      <button data-testid={`add-task-${col.id}`} onClick={(e) => { e.stopPropagation(); onQuickAdd?.(col.id); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-[12px] text-zinc-400 hover:text-zinc-600 hover:bg-white transition-all duration-150 ${hoverCol === col.id || columnTasks.length === 0 ? 'opacity-100' : 'opacity-0'}`}>
                        <Plus size={12} /> Create
                      </button>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}

        {/* Add Column Button */}
        {canEdit && (
          <div className="flex-shrink-0 w-[280px] flex flex-col" data-testid="add-column-area">
            {addingColumn ? (
              <div className="bg-zinc-50 rounded-lg border border-zinc-200 p-3 space-y-2">
                <Input value={newColName} onChange={e => setNewColName(e.target.value)} placeholder="Column name..." className="h-8 text-[12px]" autoFocus data-testid="new-column-name"
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateColumn(); if (e.key === 'Escape') setAddingColumn(false); }} />
                <div className="flex items-center gap-1">
                  {COLUMN_COLORS.map(c => (
                    <button key={c} onClick={() => setNewColColor(c)} className={`w-5 h-5 rounded-full border-2 transition-all ${newColColor === c ? 'border-zinc-800 scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={handleCreateColumn} disabled={!newColName.trim()} className="flex-1 h-7 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-medium rounded-md disabled:opacity-40 transition-colors" data-testid="confirm-new-column">Create</button>
                  <button onClick={() => setAddingColumn(false)} className="h-7 px-3 text-[11px] text-zinc-500 hover:text-zinc-700 border border-zinc-200 rounded-md"><X size={12} /></button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingColumn(true)} data-testid="add-column-button"
                className="h-10 flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-zinc-200 text-[12px] text-zinc-400 font-medium hover:border-zinc-300 hover:text-zinc-600 hover:bg-zinc-50 transition-all duration-150">
                <Plus size={14} /> Create column
              </button>
            )}
          </div>
        )}
      </div>
    </DragDropContext>
  );
}
